/**
 * AS | Progress Bar — as-ck
 * Reads config from .as-ck-pb-config elements (rendered by sections/as-ck-progress-bar.liquid).
 * Multiple sections can coexist for per-market setups — the script picks the first one
 * where enabled === true. Injects the bar HTML into #as-ck-pb-mount (in snippets/cart-drawer.liquid)
 * and manages free-gift add/remove logic on cart changes.
 */
(function () {
  'use strict';

  if (window.__asCkPbLoaded) return;
  window.__asCkPbLoaded = true;

  let _config = null;
  let _updating = false;
  let _lastCart = null;
  let _fetchTimer = null;
  let _removeQueue = Promise.resolve();

  // ─── Config ────────────────────────────────────────────────────────────────

  function getConfig() {
    if (_config) return _config;
    const els = document.querySelectorAll('.as-ck-pb-config');
    for (const el of els) {
      try {
        const cfg = JSON.parse(el.textContent);
        if (cfg.enabled) {
          _config = cfg;
          return _config;
        }
      } catch (e) {
        console.error('[as-ck-pb] Failed to parse config', e);
      }
    }
    return null;
  }

  // ─── CSS vars ─────────────────────────────────────────────────────────────

  function applyCSSVars(config) {
    const mount = document.getElementById('as-ck-pb-mount');
    if (!mount) return;
    mount.style.setProperty('--as-ck-pb-fill', config.bar_gradient);
    mount.style.setProperty('--as-ck-pb-bg', config.bar_bg_color);
    mount.style.setProperty('--as-ck-pb-border', config.icon_border_color);
    mount.style.setProperty('--as-ck-icon-size', config.icon_size + 'px');
    mount.style.setProperty('--as-ck-icon-color', config.icon_color);
  }

  // ─── Money formatting ──────────────────────────────────────────────────────

  function formatMoney(cents, format) {
    const matches = format.match(/\{\{\s*(\w+)\s*\}\}/);
    const type = matches ? matches[1] : 'amount';
    let str;
    switch (type) {
      case 'amount_no_decimals':
        str = Math.round(cents / 100).toString();
        break;
      case 'amount_with_comma_separator':
        str = (cents / 100).toFixed(2).replace('.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        str = Math.round(cents / 100).toString();
        break;
      default:
        str = (cents / 100).toFixed(2);
    }
    return format.replace(/\{\{[^}]+\}\}/, str);
  }

  // ─── Bar HTML ──────────────────────────────────────────────────────────────

  function buildBarHTML(config) {
    const thresholds = [...config.thresholds]
      .filter((t) => t.threshold_cents > 0)
      .sort((a, b) => a.threshold_cents - b.threshold_cents);
    if (thresholds.length === 0) return '';
    const max = thresholds[thresholds.length - 1].threshold_cents;

    const icons = thresholds
      .map((t, i) => {
        const pct = (t.threshold_cents / max) * 100;

        let iconContent = '';
        if (t.icon_type === 'svg_code' && t.icon_svg) {
          iconContent = t.icon_svg;
        } else if (t.icon_url) {
          iconContent = `<img src="${t.icon_url}" alt="${escAttr(t.product_title)}" loading="lazy">`;
        }

        return `<div class="as-ck-pb__icon" id="as-ck-pb-icon-${i}" style="left:${pct}%" data-idx="${i}" aria-hidden="true">${iconContent}</div>`;
      })
      .join('');

    return `
      <div class="as-ck-pb" id="as-ck-pb" aria-live="polite">
        <div class="as-ck-pb__text-above" id="as-ck-pb-above"></div>
        <div class="as-ck-pb__bar-outer">
          <div class="as-ck-pb__bar-track">
            <div class="as-ck-pb__fill" id="as-ck-pb-fill" style="width:0%"></div>
          </div>
          <div class="as-ck-pb__icons">${icons}</div>
        </div>
        <div class="as-ck-pb__text-below" id="as-ck-pb-below"></div>
      </div>
    `;
  }

  function escAttr(str) {
    return (str || '').replace(/"/g, '&quot;').replace(/</g, '&lt;');
  }

  function isRichtextEmpty(html) {
    return !html || html.replace(/<[^>]*>/g, '').trim() === '';
  }

  // ─── Inject bar into mount point ───────────────────────────────────────────

  function injectBar() {
    const config = getConfig();
    if (!config || !config.enabled) return;

    const validThresholds = config.thresholds.filter((t) => t.threshold_cents > 0);
    if (validThresholds.length === 0) return;

    const mount = document.getElementById('as-ck-pb-mount');
    if (!mount) return;

    // Mount gets wiped on every cart-body re-render — always rebuild
    mount.innerHTML = buildBarHTML(config);
    mount.hidden = false;
    applyCSSVars(config);
    // Immediately restore last known state so text never flashes empty
    if (_lastCart) {
      updateUI(_lastCart.total_price, _lastCart.items, config);
    }
    revealGiftRemoveButtons(config);
  }

  // ─── Update bar UI ─────────────────────────────────────────────────────────

  function updateUI(totalCents, cartItems, config) {
    const mount = document.getElementById('as-ck-pb-mount');
    if (!mount || !mount.querySelector('.as-ck-pb')) return;

    // Hide bar on empty cart
    const bar = document.getElementById('as-ck-pb');
    if (bar) bar.hidden = cartItems.length === 0;
    if (cartItems.length === 0) return;

    const thresholds = [...config.thresholds]
      .filter((t) => t.threshold_cents > 0)
      .sort((a, b) => a.threshold_cents - b.threshold_cents);
    if (thresholds.length === 0) return;
    const max = thresholds[thresholds.length - 1].threshold_cents;

    // Fill width
    const fillPct = Math.min((totalCents / max) * 100, 100);
    const fill = document.getElementById('as-ck-pb-fill');
    if (fill) fill.style.width = `${fillPct}%`;

    // Icon states and find highest completed / next incomplete
    let lastDoneIdx = -1;
    let nextTodoIdx = -1;

    thresholds.forEach((t, i) => {
      const icon = document.getElementById(`as-ck-pb-icon-${i}`);
      if (!icon) return;
      if (totalCents >= t.threshold_cents) {
        icon.classList.add('as-ck-pb__icon--unlocked');
        lastDoneIdx = i;
      } else {
        icon.classList.remove('as-ck-pb__icon--unlocked');
        if (nextTodoIdx === -1) nextTodoIdx = i;
      }
    });

    // Text above — from the highest completed threshold
    const aboveEl = document.getElementById('as-ck-pb-above');
    if (aboveEl) {
      const aboveRaw = lastDoneIdx >= 0 ? (thresholds[lastDoneIdx].text_above || '') : '';
      aboveEl.innerHTML = aboveRaw;
      aboveEl.hidden = isRichtextEmpty(aboveRaw);
    }

    // Text below — from the next incomplete threshold; hidden when all done
    const belowEl = document.getElementById('as-ck-pb-below');
    if (belowEl) {
      if (nextTodoIdx === -1) {
        belowEl.innerHTML = '';
        belowEl.hidden = true;
      } else {
        const next = thresholds[nextTodoIdx];
        const remaining = next.threshold_cents - totalCents;
        const formatted = formatMoney(remaining, config.money_format);
        belowEl.innerHTML = (next.text_below || '').replace(/(?:<strong>)?\[remaining\](?:<\/strong>)?/g, `<strong>${formatted}</strong>`);
        belowEl.hidden = isRichtextEmpty(next.text_below);
      }
    }
  }

  const DISMISSED_KEY = 'as_ck_pb_dismissed';
  const _dismissed = new Set();

  function syncDismissed() {
    try {
      const stored = JSON.parse(localStorage.getItem(DISMISSED_KEY) || '[]');
      if (Array.isArray(stored)) stored.forEach((id) => _dismissed.add(id));
    } catch (e) {}
    return _dismissed;
  }

  function storeDismissed() {
    try {
      localStorage.setItem(DISMISSED_KEY, JSON.stringify([..._dismissed]));
    } catch (e) {}
  }

  function getDismissed() {
    return new Set(syncDismissed());
  }

  function dismissGift(variantId) {
    syncDismissed().add(variantId);
    storeDismissed();
  }

  function undismissGift(variantId) {
    if (syncDismissed().delete(variantId)) storeDismissed();
  }

  // ─── Threshold product logic ───────────────────────────────────────────────

  async function handleThresholds(totalCents, cartItems, config) {
    if (_updating) return false;
    _updating = true;

    try {
      const thresholds = [...config.thresholds].sort((a, b) => a.threshold_cents - b.threshold_cents);

      const variantIdsToAdd = [];
      const lineKeysToRemove = [];
      const lineKeysToClamp = [];
      const dismissed = getDismissed();

      for (const t of thresholds) {
        if (!t.variant_id) continue;

        const cartItem = cartItems.find((i) => i.variant_id === t.variant_id);
        const isInCart = !!cartItem;
        const isUnlocked = totalCents >= t.threshold_cents;

        const isGiftLine = isInCart && cartItem.final_price === 0;

        if (isUnlocked) {
          if (!isInCart && !dismissed.has(t.variant_id)) {
            variantIdsToAdd.push(t.variant_id);
          } else if (isGiftLine && dismissed.has(t.variant_id)) {
            lineKeysToRemove.push(cartItem.key);
          } else if (isGiftLine && cartItem.quantity > 1) {
            lineKeysToClamp.push(cartItem.key);
          }
        } else {
          undismissGift(t.variant_id);
          if (isGiftLine) lineKeysToRemove.push(cartItem.key);
        }
      }

      let changed = false;
      if (variantIdsToAdd.length) {
        if (await cartAdd(variantIdsToAdd)) changed = true;
      }
      for (const key of lineKeysToRemove) {
        if (await cartRemove(key)) changed = true;
      }
      for (const key of lineKeysToClamp) {
        if (await cartSetQuantity(key, 1)) changed = true;
      }

      if (changed && window.eventBus) {
        window.eventBus.emit('render:cart:drawer');
      }
      return changed;
    } catch (e) {
      console.error('[as-ck-pb] Threshold error', e);
      return false;
    } finally {
      _updating = false;
    }
  }

  function revealGiftRemoveButtons(config) {
    const variantIds = new Set(config.thresholds.filter((t) => t.variant_id).map((t) => t.variant_id));
    document.querySelectorAll('[data-as-ck-gift-remove]').forEach((btn) => {
      const variantId = parseInt(btn.getAttribute('data-as-ck-gift-variant'), 10);
      btn.classList.toggle('as-ck-gift-remove--pending', !variantIds.has(variantId));
    });
  }

  function queueRemoveGift(btn) {
    if (btn.classList.contains('is-removing')) return;
    btn.classList.add('is-removing');
    _removeQueue = _removeQueue.then(() => removeGift(btn)).catch(() => {});
  }

  async function removeGift(btn) {
    const variantId = parseInt(btn.getAttribute('data-as-ck-gift-variant'), 10);
    if (!variantId) return;

    for (let attempt = 0; attempt < 2; attempt++) {
      if (attempt) await new Promise((r) => setTimeout(r, 400));

      const cart = await fetch('/cart.js')
        .then((r) => r.json())
        .catch(() => null);
      const item = cart && cart.items.find((i) => i.variant_id === variantId && i.final_price === 0);

      if (!item || (await cartRemove(item.key))) {
        dismissGift(variantId);
        if (window.eventBus) window.eventBus.emit('render:cart:drawer');
        fetchAndUpdate();
        return;
      }
    }

    btn.classList.remove('is-removing');
  }

  async function cartAdd(variantIds) {
    const ids = Array.isArray(variantIds) ? variantIds : [variantIds];
    if (await postAdd(ids)) return true;

    let anyAdded = false;
    for (const id of ids) {
      if (await postAdd([id])) anyAdded = true;
    }
    return anyAdded;
  }

  function postAdd(ids) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: ids.map((id) => ({
          id,
          quantity: 1,
          properties: { _as_ck_free_gift: '1' },
        })),
      }),
    })
      .then((r) => r.ok)
      .catch(() => false);
  }

  function cartRemove(lineItemKey) {
    return cartSetQuantity(lineItemKey, 0);
  }

  function cartSetQuantity(lineItemKey, quantity) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: lineItemKey, quantity }),
    })
      .then((r) => r.ok)
      .catch(() => false);
  }

  // ─── Qualifying total (excludes auto-added free gifts) ────────────────────

  function getQualifyingTotal(cart, config) {
    const freeVariantIds = new Set(
      config.thresholds.filter((t) => t.variant_id).map((t) => t.variant_id)
    );
    const giftTotal = cart.items.reduce((sum, item) => {
      const isFreeGift =
        freeVariantIds.has(item.variant_id) &&
        item.properties &&
        item.properties._as_ck_free_gift === '1';
      return isFreeGift ? sum + item.final_line_price : sum;
    }, 0);
    return Math.max(cart.total_price - giftTotal, 0);
  }

  // ─── Fetch cart and update everything ─────────────────────────────────────

  async function fetchAndUpdate() {
    const config = getConfig();
    if (!config || !config.enabled) return;

    try {
      const cart = await fetch('/cart.js').then((r) => r.json());
      const qualifyingTotal = getQualifyingTotal(cart, config);
      _lastCart = { total_price: qualifyingTotal, items: cart.items };
      updateUI(qualifyingTotal, cart.items, config);
      revealGiftRemoveButtons(config);

      const changed = await handleThresholds(qualifyingTotal, cart.items, config);

      const countCart = changed ? await fetch('/cart.js').then((r) => r.json()) : cart;
      syncCartCount(countCart);
    } catch (e) {
      console.error('[as-ck-pb] Fetch error', e);
    }
  }

  function syncCartCount(cart) {
    if (cart && window.wetheme && typeof window.wetheme.updateCartCount === 'function') {
      window.wetheme.updateCartCount(cart);
    }
  }

  // ─── Init ──────────────────────────────────────────────────────────────────

  function init() {
    const config = getConfig();
    if (!config || !config.enabled) return;

    injectBar();
    fetchAndUpdate();

    document.addEventListener('click', (e) => {
      const btn = e.target.closest && e.target.closest('[data-as-ck-gift-remove]');
      if (btn) queueRemoveGift(btn);
    });

    document.addEventListener('abscale:cart:updated', () => {
      injectBar();
      clearTimeout(_fetchTimer);
      _fetchTimer = setTimeout(() => fetchAndUpdate(), 300);
    });

    if (window.eventBus) {
      window.eventBus.on('update:cart:drawer', () => {
        clearTimeout(_fetchTimer);
        _fetchTimer = setTimeout(() => fetchAndUpdate(), 300);
      });

      window.eventBus.on('open:cart:drawer', () => {
        fetchAndUpdate();
      });
    }

    // Re-init if merchant changes settings in the customizer
    document.addEventListener('shopify:section:load', () => {
      _config = null;
      injectBar();
      fetchAndUpdate();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
